// components/ViewParticipantsResponses/ParticipantsResponsesBody.tsx
import React, { useState } from 'react';
import { Btn, H6, P } from '../../../AbstractElements';
import { ReviewRow } from '../../../hooks/participantResponses/useFetchResponsesForAdmin';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

function formatDate(dt?: string | Date | null) {
  if (!dt) return '—';
  try {
    const d = typeof dt === 'string' ? new Date(dt) : dt;
    return format(d, 'yyyy-MM-dd HH:mm');
  } catch {
    return String(dt);
  }
}

export default function ParticipantsResponsesBody({ reviews, loading, error } : {
  reviews?: ReviewRow[]; loading?: boolean; error?: string | null;
}) {
    const navigate=useNavigate();
  const rows = reviews ?? [];
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (loading) {
    return (
      <tbody>
        <tr><td colSpan={6}>Loading...</td></tr>
      </tbody>
    );
  }
  if (error) {
    return (
      <tbody>
        <tr><td colSpan={6} style={{ color: 'red' }}>{error}</td></tr>
      </tbody>
    );
  }
  if (rows.length === 0) {
    return (
      <tbody>
        <tr><td colSpan={6}>No submitted responses found.</td></tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {rows.map(r => {
        const pid = r.participant?._id ?? 'unknown';
        const name = `${r.participant?.firstname ?? ''} ${r.participant?.lastname ?? ''}`.trim() || (r.participant?.email ?? 'Unknown');
        // determine summary: which phases have answers
        const hasPre = Array.isArray((r as any).responses?.pre) && (r as any).responses.pre.length > 0;
        const hasDuring = Array.isArray((r as any).responses?.during) && (r as any).responses.during.length > 0;
        const hasPost = Array.isArray((r as any).responses?.post) && (r as any).responses.post.length > 0;
        const summaryParts: string[] = [];
        if (hasPre) summaryParts.push(`Pre (${(r as any).responses.pre.length})`);
        if (hasDuring) summaryParts.push(`During (${(r as any).responses.during.length})`);
        if (hasPost) summaryParts.push(`Post (${(r as any).responses.post.length})`);

        return (
          <React.Fragment key={r._id}>
            <tr>
              <td>
                <div>
                  <H6 className="mb-0">{name}</H6>
                  <P className="mb-0">{r.participant?.email}</P>
                </div>
              </td>

              <td>{r.group?.title ?? (r.assignment?.groupId ? String(r.assignment.groupId) : '—')}</td>

              <td>{r.assignment?.title ?? '—'}</td>

              <td>{summaryParts.length ? summaryParts.join(' • ') : '—'}</td>

              <td>{formatDate(r.submittedAt ?? r.lastSavedAt)}</td>

              <td className="text-end">
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn color="outline" onClick={()=>{navigate(`${process.env.PUBLIC_URL}/book_review/view/${r?.templateId}?assignmentId=${r?.assignmentId}&participantId=${r?.participant?._id}`)}}>
                    View
                  </Btn>
                </div>
              </td>
            </tr>

            {expanded[r._id] && (
              <tr className="bg-light">
                <td colSpan={6}>
                  <div style={{ padding: 12, borderRadius: 6, background: '#fff' }}>
                    <h6>Responses</h6>
                    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                      {['pre', 'during', 'post'].map((sec) => {
                        const arr = (r as any).responses?.[sec] ?? [];
                        if (!Array.isArray(arr) || arr.length === 0) return null;
                        return (
                          <div key={sec} style={{ minWidth: 220 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{sec}</strong>
                            <ul style={{ marginTop: 6 }}>
                              {arr.map((v: any, i: number) => (
                                <li key={i}><small>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</small></li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>

                    {Array.isArray(r.duringNotes) && r.duringNotes.length > 0 && (
                      <>
                        <h6 className="mt-2">During Notes</h6>
                        <ul>
                          {r.duringNotes.map((d, idx) => <li key={idx}><small>{d.text}</small></li>)}
                        </ul>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
}
